import { Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { AppStateInterface } from 'src/app/appState.interface';
import { ONE_CUSTOMER_ENDPOINT } from '../config/config.constants';
import { getCustomersSelector } from '../store/selectors';
import { ApiService } from './api.service';

/**
 * Risolve il nome di un cliente a partire dal suo uid.
 *
 * Prima fonte: la lista clienti nello store (getCustomersSelector). Per un utente di un cliente
 * figlio, però, lo store contiene solo il suo cliente e i discendenti: il padre (es. Actia), da
 * cui eredita catalogo e configurazioni, non c'è. In quel caso il nome viene chiesto una sola
 * volta al backend (ONE_CUSTOMER_ENDPOINT) e messo in cache; `changed$` avvisa chi deve ridisegnare.
 */
@Injectable({ providedIn: 'root' })
export class CustomerNameService implements OnDestroy {

  /** Emette quando un nome richiesto al backend è arrivato */
  readonly changed$ = new Subject<void>();

  private names = new Map<string, string>();
  private pending = new Set<string>();
  private storeSub: Subscription;

  constructor(private store: Store<AppStateInterface>, private api: ApiService) {
    this.storeSub = this.store.pipe(select(getCustomersSelector)).subscribe((customers: any[]) => {
      (customers || []).forEach(c => { if (c?.uid && c?.name) this.names.set(c.uid, c.name); });
    });
  }

  ngOnDestroy(): void {
    this.storeSub?.unsubscribe();
  }

  /**
   * Nome del cliente, se già noto; altrimenti avvia il recupero dal backend e restituisce
   * temporaneamente l'uid. Sicuro da chiamare da un getter di template: la richiesta parte una sola volta.
   */
  resolve(uid: string | null | undefined): string | null {
    if (!uid) return null;
    const known = this.names.get(uid);
    if (known) return known;
    this.fetch(uid);
    return uid;
  }

  private fetch(uid: string): void {
    if (this.pending.has(uid)) return;
    this.pending.add(uid);
    this.api.get(`${ONE_CUSTOMER_ENDPOINT}/${uid}`).subscribe({
      next: (customer: any) => {
        const name = customer?.name || customer?.data?.name;
        if (name) {
          this.names.set(uid, name);
          this.changed$.next();
        }
      },
      error: () => { /* nome non disponibile: resta l'uid */ },
      complete: () => this.pending.delete(uid)
    });
  }
}
